import { GameObject, Vector2, Rectangle } from '../../../types/game.types';
import { RenderManager } from '../../../core/RenderManager';
import { AudioManager } from '../../../core/AudioManager';

export class Spider implements GameObject {
  public id: string;
  public position: Vector2;
  public velocity: Vector2;
  public size: Vector2;
  public active: boolean = true;

  private renderManager: RenderManager;
  private audioManager: AudioManager;
  private speed: number = 80;
  private gameWidth: number;
  private gameHeight: number;
  private direction: Vector2 = { x: 1, y: 1 };
  private animationTime: number = 0;
  private animationFrame: number = 0;
  private lifeTime: number = 0;
  private maxLifeTime: number = 15; // Spider disappears after 15 seconds
  
  // Movement pattern
  private changeDirectionTimer: number = 0;
  private changeDirectionInterval: number = 2; // Change direction every 2 seconds

  constructor(
    id: string,
    x: number,
    y: number,
    renderManager: RenderManager,
    audioManager: AudioManager,
    gameWidth: number,
    gameHeight: number
  ) {
    this.id = id;
    this.position = { x, y };
    this.velocity = { x: this.speed * this.direction.x, y: this.speed * this.direction.y };
    this.size = { x: 16, y: 16 };
    this.renderManager = renderManager;
    this.audioManager = audioManager;
    this.gameWidth = gameWidth;
    this.gameHeight = gameHeight;
  }

  public update(deltaTime: number): void {
    // Update timers
    this.lifeTime += deltaTime;
    this.changeDirectionTimer += deltaTime;
    this.animationTime += deltaTime;
    
    // Animation
    if (this.animationTime >= 0.15) {
      this.animationTime = 0;
      this.animationFrame = (this.animationFrame + 1) % 8; // 8-frame spider animation
    }
    
    // Disappear after max lifetime
    if (this.lifeTime > this.maxLifeTime) {
      this.active = false;
      return;
    }
    
    // Change direction periodically
    if (this.changeDirectionTimer >= this.changeDirectionInterval) {
      this.changeDirection();
      this.changeDirectionTimer = 0;
    }
    
    // Update position
    this.position.x += this.velocity.x * deltaTime;
    this.position.y += this.velocity.y * deltaTime;
    
    // Bounce off screen boundaries
    if (this.position.x <= 0 || this.position.x >= this.gameWidth - this.size.x) {
      this.direction.x *= -1;
      this.velocity.x = this.speed * this.direction.x;
      this.position.x = Math.max(0, Math.min(this.gameWidth - this.size.x, this.position.x));
    }
    
    if (this.position.y <= 0 || this.position.y >= this.gameHeight - this.size.y) {
      this.direction.y *= -1;
      this.velocity.y = this.speed * this.direction.y;
      this.position.y = Math.max(0, Math.min(this.gameHeight - this.size.y, this.position.y));
    }
  }

  private changeDirection(): void {
    // Random direction change
    const angle = Math.random() * Math.PI * 2;
    this.direction.x = Math.cos(angle);
    this.direction.y = Math.sin(angle);
    
    // Vary speed slightly
    const currentSpeed = this.speed + (Math.random() - 0.5) * 40;
    this.velocity.x = currentSpeed * this.direction.x;
    this.velocity.y = currentSpeed * this.direction.y;
  }

  public render(ctx: CanvasRenderingContext2D): void {
    if (!this.active) return;

    const centerX = this.position.x + this.size.x / 2;
    const centerY = this.position.y + this.size.y / 2;

    // Spider body patterns for animation
    let pattern: number[][];
    
    // Create a walking spider animation
    switch (this.animationFrame % 4) {
      case 0:
        pattern = [
          [1,0,0,0,0,0,0,1],
          [0,1,0,0,0,0,1,0],
          [0,0,1,1,1,1,0,0],
          [0,1,1,1,1,1,1,0],
          [1,1,1,0,0,1,1,1],
          [0,1,1,1,1,1,1,0],
          [0,0,1,1,1,1,0,0],
          [1,0,0,0,0,0,0,1]
        ];
        break;
      case 1:
        pattern = [
          [0,1,0,0,0,0,1,0],
          [1,0,1,0,0,1,0,1],
          [0,0,1,1,1,1,0,0],
          [0,1,1,1,1,1,1,0],
          [1,1,1,0,0,1,1,1],
          [0,1,1,1,1,1,1,0],
          [0,0,1,1,1,1,0,0],
          [0,1,0,0,0,0,1,0]
        ];
        break;
      case 2:
        pattern = [
          [0,0,1,0,0,1,0,0],
          [0,1,0,1,1,0,1,0],
          [0,0,1,1,1,1,0,0],
          [0,1,1,1,1,1,1,0],
          [1,1,1,0,0,1,1,1],
          [0,1,1,1,1,1,1,0],
          [0,0,1,1,1,1,0,0],
          [1,0,0,1,1,0,0,1]
        ];
        break;
      case 3:
        pattern = [
          [0,1,0,0,0,0,1,0],
          [1,0,0,1,1,0,0,1],
          [0,0,1,1,1,1,0,0],
          [0,1,1,1,1,1,1,0],
          [1,1,1,0,0,1,1,1],
          [0,1,1,1,1,1,1,0],
          [0,0,1,1,1,1,0,0],
          [0,1,0,0,0,0,1,0]
        ];
        break;
      default:
        pattern = [
          [1,0,0,0,0,0,0,1],
          [0,1,0,0,0,0,1,0],
          [0,0,1,1,1,1,0,0],
          [0,1,1,1,1,1,1,0],
          [1,1,1,0,0,1,1,1],
          [0,1,1,1,1,1,1,0],
          [0,0,1,1,1,1,0,0],
          [1,0,0,0,0,0,0,1]
        ];
    }

    const pixelSize = 2;
    const color = '#FFFFFF'; // White spider

    for (let row = 0; row < pattern.length; row++) {
      for (let col = 0; col < pattern[row].length; col++) {
        if (pattern[row][col]) {
          const x = centerX - (pattern[row].length * pixelSize) / 2 + col * pixelSize;
          const y = centerY - (pattern.length * pixelSize) / 2 + row * pixelSize;
          this.renderManager.fillRect(x, y, pixelSize, pixelSize, color);
        }
      }
    }
  }

  public getBounds(): Rectangle {
    return {
      x: this.position.x,
      y: this.position.y,
      width: this.size.x,
      height: this.size.y
    };
  }

  public destroy(): void {
    this.active = false;
  }

  public getPointValue(): number {
    // Spider point values based on distance from player (official manual):
    // Close range = 900 points, Medium range = 600 points, Distant range = 300 points
    // For now, randomize but could be improved with actual distance calculation
    const values = [300, 600, 900];
    return values[Math.floor(Math.random() * values.length)];
  }
}